<template>
    <div
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
        @click="onClick"
        ref="reference"
        :class="[wrapperClass, 'w-fit max-w-full']"
    >
        <slot name="trigger" />
    </div>
    <Teleport to="body">
        <Transition
            enter-active-class="duration-300 ease-out"
            enter-from-class="transform opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="duration-200 ease-in"
            leave-from-class="opacity-100"
            leave-to-class="transform opacity-0"
        >
            <div
                v-if="isOpen && canOpen"
                @mouseenter="(event) => onMouseEnter(event, true)"
                @mouseleave="(event) => onMouseLeave(event, true)"
                :style="floatingStyles"
                class="max-w-[90%] absolute z-modal transition-opacity"
                ref="floating"
            >
                <span
                    ref="floatingArrow"
                    :style="{ left: arrowX }"
                    :class="[
                        placement === 'top'
                            ? '-bottom-[4.5px] border-b border-r'
                            : '-top-[4.5px] border-t border-l',
                        arrowColor,
                        arrowBorderColor,
                        'absolute rotate-45 aspect-square w-2 -ml-1 z-0',
                    ]"
                />
                <slot name="content" />
            </div>
        </Transition>
    </Teleport>
</template>

<script>
import { ref } from 'vue'
import {
    useFloating,
    autoUpdate,
    autoPlacement,
    shift,
    limitShift,
    offset,
    arrow,
} from '@floating-ui/vue'

export default {
    props: {
        canOpen: {
            type: Boolean,
            default: true,
        },
        delay: {
            type: Number,
            default: 500,
        },
        arrowColor: {
            type: String,
            default: 'bg-tooltip',
        },
        arrowBorderColor: {
            type: String,
            default: 'border-tooltip-border',
        },
        keepOpenOnTooltipHover: {
            type: Boolean,
            default: false,
        },
        wrapperClass: {
            type: String,
        },
    },

    data: () => ({
        isHovering: false,
        isOpen: false,
        hoverTimeout: null,
        hoverLeaveTimeout: null,
    }),

    watch: {
        isHovering() {
            if (this.isHovering) {
                this.hoverTimeout = setTimeout(() => {
                    this.isOpen = true
                }, this.delay)
            } else if (this.hoverTimeout) {
                clearTimeout(this.hoverTimeout)
            }
        },
    },

    computed: {
        arrowX() {
            return this.middlewareData.arrow?.x
                ? `${this.middlewareData.arrow?.x}px`
                : null
        },
    },

    methods: {
        onMouseEnter(event, hoveringToolTip = false) {
            if (
                !hoveringToolTip ||
                (hoveringToolTip && this.keepOpenOnTooltipHover)
            ) {
                clearTimeout(this.hoverLeaveTimeout)
                this.isHovering = true
            }
        },

        onMouseLeave(event, hoveringToolTip = false) {
            if (
                !hoveringToolTip ||
                (hoveringToolTip && this.keepOpenOnTooltipHover)
            ) {
                this.hoverLeaveTimeout = setTimeout(
                    () => {
                        this.isHovering = false
                        this.isOpen = false
                    },
                    this.keepOpenOnTooltipHover && !hoveringToolTip ? 300 : 0
                )
            }
        },

        onClick() {
            this.isOpen = !this.isOpen
        },
    },

    setup() {
        const reference = ref(null)
        const floating = ref(null)
        const floatingArrow = ref(null)

        const { floatingStyles, placement, middlewareData } = useFloating(
            reference,
            floating,
            {
                middleware: [
                    autoPlacement({
                        allowedPlacements: ['top', 'bottom'],
                    }),
                    shift({
                        limiter: limitShift({ offset: 36 }), // 36 = 20 (arrow offset) + 16 (height of arrow)
                        padding: 20,
                    }),
                    arrow({ element: floatingArrow, padding: 20 }),
                    offset({
                        mainAxis: 8,
                        crossAxis: 5,
                    }),
                ],
                whileElementsMounted: autoUpdate,
            }
        )

        return {
            placement,
            reference,
            floating,
            floatingArrow,
            floatingStyles,
            middlewareData,
        }
    },
}
</script>
